import { AppointmentDetails } from '@dreryk/egabinet-appointment-frontend-api';
import { AppointmentAdvancePayment } from '@dreryk/egabinet-appointment-advance-payment-frontend-api';
import { PhoneNumber } from '@dreryk/phone-number-common-api';

import { useFormController } from '@dreryk/react-common';
import { PhoneNumberInput } from '@dreryk/react-components';

import {
   AppointmentBeginDateTextView,
   AppointmentPatientTextView,
   AppointmentPractitionerTextView,
} from '~libs.desktop.shared.appointment';

import { AdvancePaymentPaidAmountTextView } from '../../../components/AdvancePaymentPaidAmountTextView';
import { AdvancePaymentPendingOnlinePaymentsTotalAmountTextView } from '../../../components/AdvancePaymentPendingOnlinePaymentsTotalAmountTextView';
import { AdvancePaymentRefundsTotalAmountTextView } from '../../../components/AdvancePaymentRefundsTotalAmountTextView';
import { AdvancePaymentRequiredAmountTextView } from '../../../components/AdvancePaymentRequiredAmountTextView';
import { AdvancePaymentSurchargeMethodTypeTextView } from '../../../components/AdvancePaymentSurchargeMethodTypeTextView';
import { translationKeys } from '../../../translations';
import { AdvancePaymentRemainingAmountTextView } from '../../../components/AdvancePaymentRemainingAmountTextView';
import { AdvancePaymentPhoneNumberSmsCapabilitiesNotification } from '../../../components/AdvancePaymentPhoneNumberSmsCapabilitiesNotification';

import {
   AdvancePaymentSettlementFormBean,
   AdvancePaymentSettlementFormController,
} from './AdvancePaymentSettlementForm.types';
import { OneColumnSpan, StyledForm } from './AdvancePaymentSettlementForm.styled';

type AdvancePaymentSettlementFormRenderProps = Pick<
   AdvancePaymentSettlementFormController,
   'handleSubmit' | 'formState' | 'trigger' | 'watch'
>;

type AdvancePaymentSettlementFormProps = {
   children: (renderProps: AdvancePaymentSettlementFormRenderProps) => JSX.Element;
   appointmentDetails: AppointmentDetails;
   advancePayment: AppointmentAdvancePayment;
   patientPhoneNumber: PhoneNumber | undefined;
};

export const AdvancePaymentSettlementForm = (props: AdvancePaymentSettlementFormProps): JSX.Element => {
   const { register, handleSubmit, formState, watch, trigger } = useFormController<AdvancePaymentSettlementFormBean>({
      patientPhoneNumber: props.patientPhoneNumber ?? null,
   });

   const patientPhoneNumber = watch('patientPhoneNumber');

   return (
      <StyledForm noValidate>
         <AppointmentBeginDateTextView beginDate={props.appointmentDetails.beginDate} />
         <AppointmentPatientTextView patient={props.appointmentDetails.patient} />

         <OneColumnSpan>
            <AppointmentPractitionerTextView practitioner={props.appointmentDetails.practitioner} />
         </OneColumnSpan>

         <AdvancePaymentRequiredAmountTextView requiredAmount={props.advancePayment.requiredAmount} />
         <AdvancePaymentPaidAmountTextView
            paidAmount={props.advancePayment.paidAmount}
            requiredAmount={props.advancePayment.requiredAmount}
         />

         <AdvancePaymentPendingOnlinePaymentsTotalAmountTextView
            pendingOnlinePaymentsTotalAmount={props.advancePayment.pendingOnlinePaymentsTotalAmount}
         />
         <AdvancePaymentRefundsTotalAmountTextView refundsTotalAmount={props.advancePayment.refundsTotalAmount} />

         <AdvancePaymentSurchargeMethodTypeTextView surchargeMethodType={props.advancePayment.paymentMethod} />
         <AdvancePaymentRemainingAmountTextView remainingAmount={props.advancePayment.remainingAmount} />

         <OneColumnSpan>
            <PhoneNumberInput
               controller={register('patientPhoneNumber', { required: true })}
               testId={'patientPhoneNumber'}
               label={translationKeys.phoneNumberForLinkSending}
            />
            <AdvancePaymentPhoneNumberSmsCapabilitiesNotification phoneNumber={patientPhoneNumber} />
         </OneColumnSpan>

         {props.children({
            handleSubmit: handleSubmit,
            formState: formState,
            trigger: trigger,
            watch: watch,
         })}
      </StyledForm>
   );
};
